import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Trash2,
  Calendar,
  Inbox,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E5E1D8] p-6 md:p-8 flex items-start justify-between"
    >
      <div>
        <div className="overline text-[#68766E]">{label}</div>
        <div className="font-serif text-4xl md:text-5xl text-[#111814] mt-2">
          {value}
        </div>
      </div>
      <Icon size={22} strokeWidth={1.4} className="text-[#C5A059]" />
    </div>
  );
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

export default function AdminDashboard() {
  const { user, loading, logout, authedFetch } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [iq, st] = await Promise.all([
        authedFetch("/admin/inquiries"),
        authedFetch("/admin/stats"),
      ]);
      setInquiries(iq.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user && user.username) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div
        data-testid="admin-loading"
        className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-[#68766E] text-sm tracking-[0.2em] uppercase"
      >
        Loading…
      </div>
    );
  }

  if (!user || !user.username) {
    return <Navigate to="/admin/login" replace />;
  }

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await authedFetch(`/admin/inquiries/${id}`, { method: "DELETE" });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted.");
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#FDFBF7] text-[#3A453F]"
    >
      <header className="border-b border-[#E5E1D8] bg-[#FDFBF7]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center bg-[#1A2F24] text-[#C5A059] font-serif text-xl">
              R
            </span>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#111814]">
                Admin Dashboard
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#68766E]">
                Logged in as {user.username}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              disabled={busy}
              className="btn-secondary"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="btn-primary"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-6">
        <div className="overline mb-3">
          <span className="divider-gold mr-3" /> Overview
        </div>
        <h1 className="font-serif text-3xl md:text-5xl text-[#111814] leading-tight">
          Your inbox of opportunities.
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          label="Today"
          value={stats?.today ?? "—"}
          icon={Calendar}
          testid="stat-today"
        />
        <StatCard
          label="This Week"
          value={stats?.this_week ?? "—"}
          icon={TrendingUp}
          testid="stat-week"
        />
        <StatCard
          label="This Month"
          value={stats?.this_month ?? "—"}
          icon={Inbox}
          testid="stat-month"
        />
        <StatCard
          label="All Time"
          value={stats?.total ?? "—"}
          icon={Mail}
          testid="stat-total"
        />
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div
            className="lg:col-span-7 bg-white border border-[#E5E1D8]"
            data-testid="inquiries-list"
          >
            <div className="px-6 py-4 border-b border-[#E5E1D8] flex items-center justify-between">
              <h2 className="font-serif text-2xl text-[#111814]">
                All Inquiries
              </h2>
              <span className="text-xs text-[#68766E] tracking-[0.18em] uppercase">
                {inquiries.length} total
              </span>
            </div>
            {inquiries.length === 0 && !busy && (
              <div
                className="px-6 py-16 text-center text-[#68766E]"
                data-testid="inquiries-empty"
              >
                <Inbox size={28} className="mx-auto text-[#C5A059] mb-3" />
                No inquiries yet. They will appear here as soon as someone
                submits the contact form.
              </div>
            )}
            <ul className="divide-y divide-[#E5E1D8]">
              {inquiries.map((iq) => (
                <li
                  key={iq.id}
                  onClick={() => setSelected(iq)}
                  data-testid={`inquiry-row-${iq.id}`}
                  className={`px-6 py-5 cursor-pointer transition-colors ${
                    selected?.id === iq.id
                      ? "bg-[#F5F1E8]"
                      : "hover:bg-[#FAF7F0]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-serif text-xl text-[#111814] truncate">
                        {iq.name}
                      </div>
                      <div className="text-sm text-[#68766E] mt-1 truncate">
                        {iq.email}
                        {iq.service ? ` · ${iq.service}` : ""}
                      </div>
                      <div className="text-[13px] text-[#3A453F] mt-2 line-clamp-2">
                        {iq.message}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] tracking-[0.14em] uppercase text-[#68766E]">
                        {formatDate(iq.created_at)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <aside
            className="lg:col-span-5 bg-white border border-[#E5E1D8] lg:sticky lg:top-28 self-start"
            data-testid="inquiry-detail"
          >
            {!selected ? (
              <div className="px-6 py-16 text-center text-[#68766E]">
                <Mail size={28} className="mx-auto text-[#C5A059] mb-3" />
                Select an inquiry to view details.
              </div>
            ) : (
              <div className="p-6 md:p-8">
                <div className="overline mb-2">Inquiry</div>
                <h3 className="font-serif text-3xl text-[#111814] leading-tight">
                  {selected.name}
                </h3>
                <div className="text-xs tracking-[0.14em] uppercase text-[#68766E] mt-2">
                  {formatDate(selected.created_at)}
                </div>

                <div className="mt-6 space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <Mail size={16} className="text-[#C5A059] mt-1" />
                    <a
                      href={`mailto:${selected.email}`}
                      className="text-[#1A2F24] underline-offset-4 hover:underline break-all"
                      data-testid="detail-email"
                    >
                      {selected.email}
                    </a>
                  </div>
                  {selected.phone && (
                    <div className="flex items-start gap-3">
                      <Phone size={16} className="text-[#C5A059] mt-1" />
                      <a
                        href={`tel:${selected.phone}`}
                        className="text-[#1A2F24] underline-offset-4 hover:underline"
                        data-testid="detail-phone"
                      >
                        {selected.phone}
                      </a>
                    </div>
                  )}
                  {selected.service && (
                    <div className="flex items-start gap-3">
                      <Inbox size={16} className="text-[#C5A059] mt-1" />
                      <span>{selected.service}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-[#E5E1D8]">
                  <div className="overline mb-2">Message</div>
                  <p className="text-[15px] text-[#3A453F] leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=Re:%20Your%20inquiry%20to%20Roopesh%20Kumar%20%26%20Associates`}
                    className="btn-primary flex-1 justify-center"
                    data-testid="detail-reply-btn"
                  >
                    <Mail size={14} /> Reply
                  </a>
                  <button
                    onClick={() => onDelete(selected.id)}
                    className="btn-secondary"
                    data-testid="detail-delete-btn"
                    style={{ borderColor: "#A94442", color: "#A94442" }}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
}
